import axios, { AxiosResponse } from 'axios'
import posthog from 'posthog-js'

import { UseQueryOptions, UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query'

import { CHAIN_IDS, ChainName, isTestnet } from '@src/configs/chainInfo'
import { V1_API_URL, V1_API_URL_TESTNET, V2_API_URL } from '@src/configs/env'
import { LiquiditySource, ResultCode } from '@src/enums'
import { getApiUrl, v1Api, v1TestnetApi } from '@src/libs/v1Api'
import { IOrderHistoryItem, IQuoteData, IQuoteParams, IQuoteResponse } from '@src/types/order'
import { getChecksumAddress } from '@src/utils/address'
import { isFeatureEnabled } from '@src/utils/environment'
import { createContextLogger } from '@src/utils/logger'
import { mapTokenAddress } from '@src/utils/tokenAddressMapping'

/**
 * V2 API Token Model interface for use in quotes
 */
interface V2TokenModel {
    token_id: string
    chain_id: string | number
    address: string
    symbol: string
    name: string
    decimals: number
    image: string
    price: number
    market_cap: number
    volume_24h: number
    high_24h: number
    low_24h: number
    price_change_24h: number
    price_change_percentage_24h: number
    last_updated: string
}

/**
 * V2 API Order Direction interface
 */
interface V2OrderDirection {
    src_token_address: string
    dst_token_address: string
    src_lzc: number
    dst_lzc: number
}

/**
 * V2 API Order Funding interface
 */
interface V2OrderFunding {
    src_token_quantity: string
    dst_token_quantity: string
    bond_token_address: string
    bond_token_quantity: string
    bond_fee: string
}

/**
 * V2 API Order Expiration interface
 */
interface V2OrderExpiration {
    timestamp: string
    challenge_offset: string
    challenge_window: string
}

/**
 * V2 API Order Data interface
 */
interface V2OrderData {
    src_contract_address: string
    src_wallet_address: string
    dst_wallet_address: string
    src_filler_address: string
    order_direction: V2OrderDirection
    order_funding: V2OrderFunding
    order_expiration: V2OrderExpiration
}

/**
 * V2 API Fee Details interface
 */
interface V2QuoteFees {
    gas_cost_usd: number
    relay_fee_usd: number
}

/**
 * V2 API Quote Response interface
 */
interface V2QuoteResponse {
    success: boolean
    liquidity_source: string
    src_token: V2TokenModel
    dst_token: V2TokenModel
    src_token_quantity: string
    dst_token_quantity: string
    created_at: string
    expires_at: string
    miles: number
    fees: V2QuoteFees
    order_data: V2OrderData
    message?: string // Optional error message when success is false
}

/**
 * Response from the liquidity check endpoint
 */
interface LiquidityResponse {
    supported: boolean
    sources: string[] // Can include 'marketmaker', 'cctp', etc.
    message: string
}

/**
 * V2 API Quote Request interface
 */
interface V2QuoteRequest {
    src_chain_id: string | number
    src_wallet_address: string
    src_token_address: string
    src_token_quantity: string
    dst_chain_id: string | number
    dst_wallet_address: string
    dst_token_address: string
}

/**
 * Transform V2 API quote response to legacy format
 */
const transformV2QuoteResponse = (quoteData: V2QuoteResponse): IQuoteData => {
    logger.info('Transforming V2 API quote data to legacy format')

    try {
        // Map V2 API liquidity source values to our enum values
        let liquidity_source: LiquiditySource
        switch (quoteData.liquidity_source) {
            case 'cctp':
                liquidity_source = LiquiditySource.cctpDirect
                break
            case 'marketmaker':
                liquidity_source = LiquiditySource.marketMaker
                break
            case 'unavailable':
                liquidity_source = LiquiditySource.unavailable
                break
            default:
                logger.warn(`Unknown liquidity source: ${quoteData.liquidity_source}, defaulting to unavailable`)
                liquidity_source = LiquiditySource.unavailable
        }

        // Parse critical numeric values
        const src_token_quantity = BigInt(quoteData.src_token_quantity)
        const dst_token_quantity = BigInt(quoteData.dst_token_quantity)
        const bond_token_quantity = BigInt(quoteData.order_data.order_funding.bond_token_quantity)
        const bond_fee = Number(quoteData.order_data.order_funding.bond_fee)

        // Extract chain information
        const src_chain =
            Object.entries(CHAIN_IDS)
                .find(
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    ([_name, id]) => id.toString() === quoteData.src_token.chain_id.toString()
                )?.[0]
                .toLowerCase() ?? ''

        const dst_chain =
            Object.entries(CHAIN_IDS)
                .find(
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    ([_name, id]) => id.toString() === quoteData.dst_token.chain_id.toString()
                )?.[0]
                .toLowerCase() ?? ''

        // Calculate expiration timestamps
        const created_at = quoteData.created_at
        const expires_at = quoteData.expires_at

        // Parse challenge window parameters
        const challenge_offset = Number(quoteData.order_data.order_expiration.challenge_offset)
        const challenge_window = Number(quoteData.order_data.order_expiration.challenge_window)

        // --- Refactor: Fully adapt V2OrderData to IQuoteData expectations (type-safe, no bracket notation) ---
        const v2OrderData = quoteData.order_data
        const adaptedOrderData = {
            src_contract_address: v2OrderData.src_contract_address,
            src_wallet_address: v2OrderData.src_wallet_address,
            dst_wallet_address: v2OrderData.dst_wallet_address,
            src_filler_address: v2OrderData.src_filler_address,
            // Add normalized fields for downstream compatibility
            filler_address: v2OrderData.src_filler_address, // V2 only has src_filler_address
            target_address: v2OrderData.dst_wallet_address, // V2 only has dst_wallet_address
            // Optionally, camelCase for downstream code
            fillerAddress: v2OrderData.src_filler_address,
            targetAddress: v2OrderData.dst_wallet_address,
            // Adapt nested fields
            order_direction: {
                // Use only V1 format property names
                src_token_address: v2OrderData.order_direction?.src_token_address ?? '',
                dst_token_address: v2OrderData.order_direction?.dst_token_address ?? '',
                dst_lzc: v2OrderData.order_direction?.dst_lzc ?? 0,
            },
            order_funding: {
                // Use only V1 format property names
                src_amount_in: v2OrderData.order_funding?.src_token_quantity ?? '0',
                dst_amount_out: v2OrderData.order_funding?.dst_token_quantity ?? '0',
                bond_fee: Number(v2OrderData.order_funding?.bond_fee ?? 0),
                bond_token_address: v2OrderData.order_funding?.bond_token_address ?? '',
                bond_amount: v2OrderData.order_funding?.bond_token_quantity ?? '0',
            },
            order_expiration: {
                timestamp: Number(v2OrderData.order_expiration?.timestamp ?? 0),
                challengeOffset: Number(v2OrderData.order_expiration?.challenge_offset ?? 0),
                challengeWindow: Number(v2OrderData.order_expiration?.challenge_window ?? 0),
            },
        }

        return {
            src_amount: src_token_quantity,
            dst_amount: dst_token_quantity,
            bond_amount: bond_token_quantity,
            bond_asset_address: quoteData.order_data.order_funding.bond_token_address,
            bond_fee: bond_fee,
            challenge_offset: challenge_offset,
            challenge_window: challenge_window,
            created_at: created_at,
            dst_asset_address: quoteData.dst_token.address,
            dst_chain: dst_chain,
            expired: false, // We assume it's not expired since we just received it
            expires_at: expires_at,
            id: 0, // V2 API doesn't provide an ID in the same way
            invalid_amount: false, // Success flag would be false if invalid
            liquidity_source: liquidity_source,
            src_asset_address: quoteData.src_token.address,
            src_chain: src_chain,
            wallet_address: quoteData.order_data.src_wallet_address,
            order_data: adaptedOrderData,
            reject_order: !quoteData.success,
        }
    } catch (error) {
        logger.error('Error transforming V2 quote response:', error)
        throw new Error('Failed to transform V2 quote response')
    }
}

/**
 * V2 API Order Request interface
 */
interface V2OrderRequest {
    src_chain_id: string | number
    src_tx_id: string
}

/**
 * V2 API Order Response interface
 */
interface V2OrderResponse {
    id: string
    type: string // default | cctp
    src_chain_id: string | number
    dst_chain_id: string | number
    src_wallet_address: string
    dst_wallet_address: string
    src_token: V2TokenModel
    dst_token: V2TokenModel
    src_token_quantity: string
    dst_token_quantity: string
    created_at: string
    filled_at: string | null // null if not filled
    expires_at: string
    place_tx_id: string
    fill_tx_id: string | null // null if not filled
    completed: boolean
    eta: number
}

/**
 * Transform V2 API order response to legacy format
 */
const transformV2OrderResponse = (orderData: V2OrderResponse): IOrderHistoryItem => {
    logger.info('Transforming V2 API order data to legacy format')

    try {
        // Find chain names from chain IDs
        const src_chain =
            Object.entries(CHAIN_IDS)
                .find(
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    ([_name, id]) => id.toString() === orderData.src_chain_id.toString()
                )?.[0]
                .toLowerCase() ?? ''

        const dst_chain =
            Object.entries(CHAIN_IDS)
                .find(
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    ([_name, id]) => id.toString() === orderData.dst_chain_id.toString()
                )?.[0]
                .toLowerCase() ?? ''

        return {
            id: orderData.id,
            taker_address: orderData.src_wallet_address,
            maker_address: null, // V2 API doesn't provide this field
            src_asset_address: orderData.src_token.address,
            dst_asset_address: orderData.dst_token.address,
            src_chain: src_chain,
            dst_chain: dst_chain,
            src_amount: orderData.src_token_quantity,
            dst_amount: orderData.dst_token_quantity,
            created_at: orderData.created_at,
            filled_at: orderData.filled_at,
            expires_at: orderData.expires_at,
            completed: orderData.completed,
            place_tx: orderData.place_tx_id,
            fill_tx: orderData.fill_tx_id,
            eta: orderData.eta,
            order_type: orderData.type as 'default' | 'cctp',
        }
    } catch (error) {
        logger.error('Error transforming V2 order response:', error)
        throw new Error('Failed to transform V2 order response')
    }
}

const logger = createContextLogger('order.api')

/**
 * Get order history using the legacy API
 */
const getLegacyOrderHistory = async (address: string): Promise<IOrderHistoryItem[]> => {
    logger.info('Getting order history using legacy API', { address })
    const endpoint = `/v1/orders?wallet=${address}`
    const apiCalls = [v1Api.get<IOrderHistoryItem[]>(endpoint)]

    if (isFeatureEnabled('USE_TESTNETS_ONLY') || isFeatureEnabled('ENABLE_TESTNETS')) {
        apiCalls.push(v1TestnetApi.get<IOrderHistoryItem[]>(endpoint))
    }

    const [mainnetResult, testnetResult] = await Promise.allSettled(apiCalls)

    let orders: IOrderHistoryItem[] = []

    // Process mainnet result.
    if (mainnetResult && mainnetResult.status === 'fulfilled') {
        orders = orders.concat(mainnetResult.value.data)
    } else {
        logger.error(`[getLegacyOrderHistory] Mainnet API failed.`)
    }

    // Process testnet result.
    if (testnetResult && testnetResult.status === 'fulfilled') {
        orders = orders.concat(testnetResult.value.data)
    } else {
        logger.error(`[getLegacyOrderHistory] Testnet API failed.`)
    }

    return orders
}

/**
 * Get order history using the V2 API
 */
const getV2OrderHistory = async (address: string): Promise<IOrderHistoryItem[]> => {
    logger.info('Getting order history using V2 API', { address })
    const endpoint = `/wallets/${address}/orders`

    try {
        // Make API requests to both mainnet and testnet if enabled
        const apiUrl = getApiUrl()
        const apiCalls = [
            apiUrl ? axios.get<V2OrderResponse[]>(`${apiUrl}${endpoint}`) : Promise.reject('V2 API URL not available'),
        ]

        if (isFeatureEnabled('USE_TESTNETS_ONLY') || isFeatureEnabled('ENABLE_TESTNETS')) {
            // If we have a testnet V2 API URL configured, use it
            const testnetApiUrl = getApiUrl('testnet')
            if (testnetApiUrl) {
                apiCalls.push(axios.get<V2OrderResponse[]>(`${testnetApiUrl}${endpoint}`))
            }
        }

        const [mainnetResult, testnetResult] = await Promise.allSettled<AxiosResponse<V2OrderResponse[]>>(apiCalls)

        let orders: IOrderHistoryItem[] = []

        // Process mainnet result
        if (mainnetResult.status === 'fulfilled' && mainnetResult.value.data) {
            // Transform each order from V2 format to legacy format
            const transformedOrders = mainnetResult.value.data.map(transformV2OrderResponse)
            orders = [...orders, ...transformedOrders]
            logger.info(`[getV2OrderHistory] Successfully retrieved ${transformedOrders.length} orders from mainnet`)
        } else if (mainnetResult.status === 'rejected') {
            const reason = mainnetResult.reason ? JSON.stringify(mainnetResult.reason) : 'unknown error'
            logger.error(`[getV2OrderHistory] Mainnet API failed: ${reason}`)
        }

        // Process testnet result if applicable
        if (testnetResult && testnetResult.status === 'fulfilled' && testnetResult.value.data) {
            const transformedOrders = testnetResult.value.data.map(transformV2OrderResponse)
            orders = [...orders, ...transformedOrders]
            logger.info(`[getV2OrderHistory] Successfully retrieved ${transformedOrders.length} orders from testnet`)
        } else if (testnetResult && testnetResult.status === 'rejected') {
            const reason = testnetResult.reason ? JSON.stringify(testnetResult.reason) : 'unknown error'
            logger.error(`[getV2OrderHistory] Testnet API failed: ${reason}`)
        }

        return orders
    } catch (error) {
        logger.error('Error fetching orders from V2 API', error)
        throw error
    }
}

export const apiGetOrderHistory = async (address: string): Promise<IOrderHistoryItem[]> => {
    // Track the API version used
    const useV2Api = isFeatureEnabled('V2_MIGRATION')
    posthog.capture('fetch_order_history', {
        api_version: useV2Api ? 'v2' : 'v1',
        address: address,
    })

    let orders: IOrderHistoryItem[] = []

    if (useV2Api) {
        try {
            // Try V2 API first
            orders = await getV2OrderHistory(address)
            logger.info(`[apiGetOrderHistory] Successfully retrieved ${orders.length} orders using V2 API`)
        } catch (error) {
            // Fall back to legacy API if V2 fails
            logger.warn('V2 API order history failed, falling back to legacy API', error)
            try {
                orders = await getLegacyOrderHistory(address)
                logger.info(
                    `[apiGetOrderHistory] Successfully retrieved ${orders.length} orders using legacy API after V2 failure`
                )
            } catch (fallbackError) {
                logger.error('Both V2 and legacy API failed for order history', { originalError: error, fallbackError })
                // Return empty array if both APIs fail
                return []
            }
        }
    } else {
        // Use legacy API directly if feature flag is not enabled
        orders = await getLegacyOrderHistory(address)
    }

    // Deduplicate orders based on a unique identifier (e.g., order ID)
    const uniqueOrders = Array.from(new Map(orders.map(order => [order.id, order])).values())

    return uniqueOrders
}

/**
 * Submit order using the legacy API
 */
const submitLegacyOrder = async (orderData: any, referralCode?: string, referrer?: string) => {
    logger.info('Sending order data using legacy API')

    // Remove space from sellChain
    const sellChain = orderData.sellChain.replace(/\s+/g, '')
    const payload = {
        chain: sellChain,
        place_taker_tx: orderData.transactionHash,
        referral_code: referralCode,
        referrer,
    }

    let api = null
    let response

    if (!isTestnet(CHAIN_IDS[payload.chain as ChainName])) {
        logger.info(`[submitLegacyOrder] Using mainnet API (url = ${V1_API_URL})\npayload.chain = ${payload.chain}`)
        api = v1Api
    } else {
        logger.info(
            `[submitLegacyOrder] Using testnet API (url = ${V1_API_URL_TESTNET})\npayload.chain = ${payload.chain}`
        )
        api = v1TestnetApi
    }

    response = await api.post('/v1/orders', payload)
    logger.info('Legacy Order API response received', { status: response?.status })

    return response
}

/**
 * Submit order using the V2 API
 */
const submitV2Order = async (orderData: any, referralCode?: string, referrer?: string) => {
    logger.info('Sending order data using V2 API')

    // Check if we have V2 API URL available
    const apiUrl = getApiUrl()
    if (!apiUrl) {
        throw new Error('V2 API URL not available')
    }

    // Determine if the chain is a testnet to select correct API URL
    const sellChain = orderData.sellChain.replace(/\s+/g, '')
    const chainId = CHAIN_IDS[sellChain as ChainName]
    const isTestnetChain = isTestnet(chainId)
    const endpoint = '/orders'

    // Build the V2 API request payload
    const payload: V2OrderRequest = {
        src_chain_id: chainId.toString(),
        src_tx_id: orderData.transactionHash,
    }

    // Add referral information if available
    if (referralCode || referrer) {
        // Tracking info for analytics
        logger.info('Including referral information in V2 order', {
            referral_code: referralCode,
            referrer: referrer,
        })
    }

    // Select the appropriate API URL based on whether the chain is testnet or mainnet
    const url = isTestnetChain ? getApiUrl('testnet') : apiUrl

    logger.info(`[submitV2Order] Using API (url = ${url})\npayload.chain_id = ${payload.src_chain_id}`)

    const response = await axios.post(`${url}${endpoint}`, payload)
    logger.info('V2 Order API response received', { status: response?.status })

    return response
}

export const apiSendOrderData = async (orderData: any, referralCode?: string, referrer?: string) => {
    if (orderData === undefined) {
        logger.error('Invalid order data received', { orderData })
        posthog.capture('api_send_order_failed', {
            error: 'Error decoding order data',
            error_type: 'InvalidInput',
        })
        return { message: 'Error decoding order data', status: ResultCode.failure }
    }

    // Track the API version used
    const useV2Api = isFeatureEnabled('V2_MIGRATION')
    posthog.capture('send_order', {
        api_version: useV2Api ? 'v2' : 'v1',
        chain: orderData.sellChain,
        has_referral: !!referralCode,
    })

    // Define the payload to be used in logging and error handling
    const orderInfo = {
        sellChain: orderData.sellChain,
        transactionHash: orderData.transactionHash,
    }

    logger.info('Sending order data', {
        sellChain: orderData.sellChain,
        tx: orderData.transactionHash,
        api_version: useV2Api ? 'v2' : 'v1',
    })

    let response

    try {
        if (useV2Api) {
            // When V2 is enabled, use V2 API without fallback
            response = await submitV2Order(orderData, referralCode, referrer)
            logger.info('Successfully submitted order using V2 API ', { orderId: response.data.id })
        } else {
            // Use legacy API directly if feature flag is not enabled
            response = await submitLegacyOrder(orderData, referralCode, referrer)
        }

        switch (Number(response.status)) {
            case 200:
                logger.info('Order successfully sent', {
                    orderId: response.data.id,
                    chain: orderData.sellChain,
                })
                posthog.capture('api_send_order_success', {
                    chain: orderData.sellChain,
                    has_referral: !!referralCode,
                    status: 200,
                })
                return {
                    message: 'Order successfully sent.',
                    status: ResultCode.pending,
                    eta: response.data.eta,
                    id: response.data.id,
                }
            case 202:
                logger.info('Order accepted with delay', {
                    chain: orderData.sellChain,
                    eta: response.data.eta,
                })
                posthog.capture('api_send_order_success', {
                    chain: orderData.sellChain,
                    has_referral: !!referralCode,
                    status: 202,
                })
                return {
                    message: 'Order accepted but will take 5-20 minutes to find a match.',
                    status: ResultCode.pending,
                    eta: response.data.eta,
                    id: response.data.id,
                }
            case 400:
                logger.warn('Order rejected by market maker', {
                    chain: orderData.sellChain,
                    error: response.data,
                })
                posthog.capture('api_send_order_failed', {
                    chain: orderData.sellChain,
                    error_type: 'MarketMakerRejection',
                    error_data: response.data,
                })
                return {
                    message: 'Order will not be filled by the market maker at this time.',
                    status: ResultCode.failure,
                    errorobj: response.data,
                }
            case 422:
                logger.error('Invalid payload sent to API', {
                    chain: orderData.sellChain,
                    error: response.data,
                    payload: orderInfo,
                })
                posthog.capture('api_send_order_failed', {
                    chain: orderData.sellChain,
                    error_type: 'InvalidPayload',
                    error_data: response.data,
                })
                return {
                    message: 'Error in the payload.',
                    status: ResultCode.failure,
                    errorobj: response.data,
                }
            default:
                logger.error('Unexpected API response', {
                    status: response.status,
                    chain: orderData.sellChain,
                    error: response.data,
                })
                posthog.capture('api_send_order_failed', {
                    chain: orderData.sellChain,
                    error_type: 'UnexpectedStatus',
                    status: response.status,
                    error_data: response.data,
                })
                return {
                    message: 'Unexpected error occurred.',
                    status: ResultCode.failure,
                    errorobj: response.data,
                }
        }
    } catch (error) {
        logger.error('[apiSendOrderData] Failed to send order', {
            error: error instanceof Error ? error.message : String(error),
            chain: orderData.sellChain,
        })

        // Extract detailed error information
        const errorResponse = (error as any).response
        const errorData = errorResponse?.data

        // Try to get a meaningful error message from the backend
        const serverMessage =
            (error as any).formattedMessage ||
            (typeof errorData === 'object' && errorData?.message ? errorData.message : null) ||
            (typeof errorData === 'string' ? errorData : null) ||
            errorResponse?.statusText ||
            'An unspecified error occurred.'

        posthog.capture('api_send_order_failed', {
            chain: orderData.sellChain,
            error_type: 'NetworkError',
            error_message: serverMessage,
            error_object: error instanceof Error ? error.message : String(error),
        })

        return {
            message: serverMessage,
            status: ResultCode.failure,
            errorobj: error,
            response: errorResponse,
        }
    }
}

/**
 * Helper function to get a quote using the legacy API
 */
async function getLegacyQuote(
    walletAddress: string,
    srcChain: ChainName,
    dstChain: ChainName,
    srcAssetAddress: string,
    dstAssetAddress: string,
    srcAmount: bigint,
    targetAddress: string | undefined,
    signal?: AbortSignal,
    quoteStartTime?: number
): Promise<IQuoteResponse> {
    // Use the start time if provided, otherwise set a new one
    const startTime = quoteStartTime || performance.now()

    // Prepare the legacy payload
    let payload
    if (!!targetAddress) {
        payload = {
            wallet_address: getChecksumAddress(walletAddress),
            src_chain: srcChain.toLowerCase(),
            dst_chain: dstChain.toLowerCase(),
            src_asset_address: getChecksumAddress(mapTokenAddress(srcAssetAddress, true, srcChain)),
            dst_asset_address: getChecksumAddress(mapTokenAddress(dstAssetAddress, true, dstChain)),
            src_amount: `${srcAmount}`,
            target_address: targetAddress,
        }
    } else {
        payload = {
            wallet_address: getChecksumAddress(walletAddress),
            src_chain: srcChain.toLowerCase(),
            dst_chain: dstChain.toLowerCase(),
            src_asset_address: getChecksumAddress(mapTokenAddress(srcAssetAddress, true, srcChain)),
            dst_asset_address: getChecksumAddress(mapTokenAddress(dstAssetAddress, true, dstChain)),
            src_amount: `${srcAmount}`,
        }
    }

    logger.info('[getLegacyQuote] Legacy quote requested', payload)

    try {
        let api = null

        if (!isTestnet(CHAIN_IDS[srcChain]) && !isTestnet(CHAIN_IDS[dstChain])) {
            logger.info(`[getLegacyQuote] Using mainnet API (url = ${V1_API_URL})`)
            api = v1Api
        } else if (isTestnet(CHAIN_IDS[srcChain]) && isTestnet(CHAIN_IDS[dstChain])) {
            logger.info(`[getLegacyQuote] Using testnet API (url = ${V1_API_URL_TESTNET})`)
            api = v1TestnetApi
        } else {
            logger.error(`Unable to fetch testnet <-> mainnet quote.\n(srcChain=${srcChain} dstChain=${dstChain})`)
            throw new Error('Unable to fetch testnet <-> mainnet quote.')
        }

        const response = await api.post<any>('/v1/quotes', payload, {
            signal,
        })

        logger.info('[getLegacyQuote] Legacy quote response received', { status: response.status })

        switch (response.status) {
            case 200: {
                // Extract the numeric values using regex to avoid any parsing
                const extractNumber = (field: string) => {
                    // Regex to match both quoted ("1234") and unquoted (1234) numbers
                    // e.g. consume both src_amount:1000 and src_amount:"1000"
                    const regexstr = `"${field}":\\"?([0-9]+)\\"?` // Use \\"? to match optional quotes around the number
                    const regex = new RegExp(regexstr)
                    const match = response.request.response.match(regex)
                    return match ? BigInt(match[1].toString()) : null
                }

                type NumericFields = {
                    src_amount: bigint | null
                    dst_amount: bigint | null
                    bond_amount: bigint | null
                    bond_fee: bigint | null
                }

                const rawNetworkResponse: NumericFields = {
                    src_amount: extractNumber('src_amount'),
                    dst_amount: extractNumber('dst_amount'),
                    bond_amount: extractNumber('bond_amount'),
                    bond_fee: extractNumber('bond_fee'),
                }

                const missingFields: string[] = []

                // Check for missing fields and log details
                if (rawNetworkResponse.src_amount === null || rawNetworkResponse.src_amount === undefined) {
                    missingFields.push(`src_amount (expected a numeric value, got ${rawNetworkResponse.src_amount})`)
                }
                if (rawNetworkResponse.dst_amount === null || rawNetworkResponse.dst_amount === undefined) {
                    missingFields.push(`dst_amount (expected a numeric value, got ${rawNetworkResponse.dst_amount})`)
                }
                if (rawNetworkResponse.bond_amount === null || rawNetworkResponse.bond_amount === undefined) {
                    missingFields.push(`bond_amount (expected a numeric value, got ${rawNetworkResponse.bond_amount})`)
                }

                // Return error if any fields are missing
                if (missingFields.length > 0) {
                    posthog.capture('quote_failed', {
                        error_type: 'MissingFields',
                        missing_fields: missingFields,
                        src_chain: srcChain,
                        dst_chain: dstChain,
                        using_v2_api: false,
                    })
                    return {
                        status: ResultCode.failure,
                        errorMsg: `Missing required numeric values in response: ${missingFields.join(', ')}`,
                        quote: null,
                    }
                } else {
                    const quoteData = {
                        ...response.data,
                        src_amount: rawNetworkResponse.src_amount ?? BigInt(0), // actually will never be zero, just put there to silence compilation error.
                        dst_amount: rawNetworkResponse.dst_amount ?? BigInt(0), // actually will never be zero, just put there to silence compilation error.
                        bond_amount: rawNetworkResponse.bond_amount ?? BigInt(0), // actually will never be zero, just put there to silence compilation error.
                    }

                    logger.info('[getLegacyQuote] Legacy quote processed', {
                        liquidity_source: quoteData.liquidity_source,
                    })

                    if (quoteData.liquidity_source && quoteData.liquidity_source == 'unavailable') {
                        posthog.capture('quote_failed', {
                            error_type: 'LiquidityUnavailable',
                            src_chain: srcChain,
                            dst_chain: dstChain,
                            src_asset: srcAssetAddress,
                            dst_asset: dstAssetAddress,
                            using_v2_api: false,
                        })
                        return {
                            status: ResultCode.failure,
                            quote: quoteData as IQuoteData,
                            errorMsg: 'Liquidity source unavailable.',
                        }
                    }

                    posthog.capture('quote_success', {
                        src_chain: srcChain,
                        dst_chain: dstChain,
                        src_asset_address: srcAssetAddress,
                        dst_asset_address: dstAssetAddress,
                        src_amount: srcAmount.toString(),
                        dst_amount: quoteData.dst_amount.toString(),
                        bond_amount: quoteData.bond_amount.toString(),
                        quote_time_ms: performance.now() - startTime,
                        has_target_address: !!targetAddress,
                        liquidity_source: quoteData.liquidity_source,
                        using_v2_api: false,
                    })
                    return {
                        status: ResultCode.success,
                        quote: quoteData as IQuoteData,
                    }
                }
            }

            case 400:
            case 500:
                posthog.capture('quote_failed', {
                    error_type: 'ApiError',
                    status: response.status,
                    error_message: response.statusText,
                    src_chain: srcChain,
                    dst_chain: dstChain,
                    using_v2_api: false,
                })
                return {
                    status: ResultCode.failure,
                    quote: null,
                    errorMsg: response.statusText,
                }

            default:
                posthog.capture('quote_failed', {
                    error_type: 'UnexpectedStatus',
                    status: response.status,
                    src_chain: srcChain,
                    dst_chain: dstChain,
                    using_v2_api: false,
                })
                return {
                    status: ResultCode.failure,
                    quote: null,
                }
        }
    } catch (error) {
        posthog.capture('quote_failed', {
            error_type: 'NetworkError',
            error_message: error instanceof Error ? error.message : 'Unknown error',
            src_chain: srcChain,
            dst_chain: dstChain,
            using_v2_api: false,
        })
        return {
            errorMsg: error instanceof Error ? error.message : 'An unknown error occurred',
            status: ResultCode.failure,
            quote: null,
        }
    }
}

/**
 * Check if liquidity is available for a given source/destination pair
 * Only available when V2_MIGRATION is enabled
 *
 * @param srcChain Source chain ID
 * @param srcAssetAddress Source token address
 * @param dstChain Destination chain ID
 * @param dstAssetAddress Destination token address
 * @param signal Optional AbortSignal for cancellation
 * @returns Object with supported flag, available sources, and message
 */
export const checkLiquidityAvailability = async (
    srcChain: string | number,
    srcAssetAddress: string,
    dstChain: string | number,
    dstAssetAddress: string,
    signal?: AbortSignal
): Promise<{
    supported: boolean
    sources: string[]
    message: string
    error?: string
}> => {
    const quoteStartTime = performance.now()

    // If V2_MIGRATION is not enabled, always return supported=true
    // since the legacy API doesn't have a liquidity check endpoint
    if (!isFeatureEnabled('V2_MIGRATION') || !V2_API_URL) {
        logger.info('[checkLiquidityAvailability] V2_MIGRATION not enabled, skipping liquidity check')
        return {
            supported: true, // Always assume liquidity is available in V1
            sources: ['legacy'],
            message: 'Using legacy API, liquidity check skipped',
        }
    }

    try {
        // Use the V2 API URL for liquidity checks
        const apiUrl = V2_API_URL

        logger.info(
            `[checkLiquidityAvailability] Checking liquidity for ${srcAssetAddress} from ${srcChain} to ${dstChain}`,
            {
                srcChain,
                dstChain,
                srcAssetAddress,
                dstAssetAddress,
                usingV2Api: isFeatureEnabled('V2_MIGRATION') && !!V2_API_URL,
            }
        )

        // Create API instance
        const api = axios.create({
            baseURL: apiUrl,
        })

        // Build the URL with query parameters
        const url = `/quotes/liquidity?src_chain_id=${srcChain}&src_token_address=${srcAssetAddress}&dst_chain_id=${dstChain}&dst_token_address=${dstAssetAddress}`

        // Make the API call
        const response = await api.get<LiquidityResponse>(url, { signal })

        if (response.status === 200) {
            const data = response.data

            logger.info('[checkLiquidityAvailability] Liquidity check response received', {
                supported: data.supported,
                sources: data.sources,
                message: data.message,
                responseTime: `${performance.now() - quoteStartTime}ms`,
            })

            // Track the liquidity check in analytics
            posthog.capture('liquidity_check', {
                src_chain: srcChain,
                dst_chain: dstChain,
                src_asset: srcAssetAddress,
                dst_asset: dstAssetAddress,
                supported: data.supported,
                sources: data.sources,
                check_time_ms: performance.now() - quoteStartTime,
            })

            return {
                supported: data.supported,
                sources: data.sources || [],
                message: data.message || (data.supported ? 'Liquidity available' : 'Liquidity not available'),
            }
        } else {
            logger.error(`[checkLiquidityAvailability] API returned non-200 status: ${response.status}`, {
                status: response.status,
                statusText: response.statusText,
            })

            return {
                supported: false,
                sources: [],
                message: 'Error checking liquidity',
                error: `API error: ${response.statusText}`,
            }
        }
    } catch (error) {
        // Handle errors
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'

        logger.error('[checkLiquidityAvailability] Error checking liquidity', {
            error: errorMessage,
            srcChain,
            dstChain,
        })

        return {
            supported: false,
            sources: [],
            message: 'Error checking liquidity',
            error: errorMessage,
        }
    }
}

export const getQuote = async (
    { walletAddress, srcChain, dstChain, srcAssetAddress, dstAssetAddress, srcAmount, targetAddress }: IQuoteParams,
    signal?: AbortSignal
): Promise<IQuoteResponse> => {
    // Add quote request tracking
    const quoteStartTime = performance.now()

    // Track the quote request
    posthog.capture('quote_requested', {
        src_chain: srcChain.toLowerCase(),
        dst_chain: dstChain.toLowerCase(),
        src_asset_address: srcAssetAddress,
        dst_asset_address: dstAssetAddress,
        src_amount: srcAmount.toString(),
        wallet_address: walletAddress,
        has_target_address: !!targetAddress,
        using_v2_api: isFeatureEnabled('V2_MIGRATION'),
    })

    // Prepare a clean wallet address (checksummed)
    const cleanWalletAddress = getChecksumAddress(walletAddress)

    // Get chain IDs
    const srcChainId = CHAIN_IDS[srcChain as ChainName]
    const dstChainId = CHAIN_IDS[dstChain as ChainName]

    // Clean and map token addresses
    const cleanSrcAssetAddress = getChecksumAddress(mapTokenAddress(srcAssetAddress, true, srcChain as ChainName))
    const cleanDstAssetAddress = getChecksumAddress(mapTokenAddress(dstAssetAddress, true, dstChain as ChainName))

    // Convert amount to string
    const srcAmountStr = srcAmount.toString()

    logger.info(`[getQuote] Quote requested for ${srcAmountStr} ${srcAssetAddress} from ${srcChain} to ${dstChain}`)

    try {
        // Check if we should be using V2 API
        const useV2Api = isFeatureEnabled('V2_MIGRATION')

        if (useV2Api) {
            logger.info('[getQuote] Using V2 API for quote')

            // Prepare V2 API payload
            const v2Payload: V2QuoteRequest = {
                src_chain_id: srcChainId,
                src_wallet_address: cleanWalletAddress,
                src_token_address: cleanSrcAssetAddress,
                src_token_quantity: srcAmountStr,
                dst_chain_id: dstChainId,
                dst_wallet_address: targetAddress || cleanWalletAddress, // Use target address if provided
                dst_token_address: cleanDstAssetAddress,
            }

            logger.info('[getQuote] V2 Quote requested', v2Payload)

            try {
                // Determine which API to use (testnet vs mainnet)
                let api = null

                if (!isTestnet(srcChainId) && !isTestnet(dstChainId)) {
                    logger.info(`[getQuote] Using mainnet API (url = ${V1_API_URL})`)
                    api = v1Api
                } else if (isTestnet(srcChainId) && isTestnet(dstChainId)) {
                    logger.info(`[getQuote] Using testnet API (url = ${V1_API_URL_TESTNET})`)
                    api = v1TestnetApi
                } else {
                    logger.error(
                        `Unable to fetch testnet <-> mainnet quote.\n(srcChain=${srcChain} dstChain=${dstChain})`
                    )
                    throw new Error('Unable to fetch testnet <-> mainnet quote.')
                }

                // Make the V2 API call
                const response = await api.post<V2QuoteResponse>('/quotes/', v2Payload, {
                    signal,
                })

                logger.info('[getQuote] V2 Quote response received', { status: response.status })

                if (response.status === 200) {
                    const v2QuoteData = response.data

                    // Check if liquidity source is unavailable or success is false
                    if (v2QuoteData.liquidity_source === 'unavailable' || !v2QuoteData.success) {
                        // Get the error message from the response if available, otherwise use default
                        const errorMessage = v2QuoteData.message || 'Liquidity source unavailable.'

                        logger.warn('[getQuote] V2 Quote failed', {
                            liquidity_source: v2QuoteData.liquidity_source,
                            success: v2QuoteData.success,
                            message: v2QuoteData.message,
                        })

                        posthog.capture('quote_failed', {
                            error_type: 'LiquidityUnavailable',
                            error_message: errorMessage,
                            src_chain: srcChain,
                            dst_chain: dstChain,
                            src_asset: srcAssetAddress,
                            dst_asset: dstAssetAddress,
                            using_v2_api: true,
                        })

                        return {
                            status: ResultCode.failure,
                            quote: null,
                            errorMsg: errorMessage,
                        }
                    }

                    try {
                        // Transform V2 response to legacy format
                        const transformedQuote = transformV2QuoteResponse(v2QuoteData)

                        logger.info('[getQuote] V2 Quote transformed successfully')

                        // Track successful quote
                        posthog.capture('quote_success', {
                            src_chain: srcChain,
                            dst_chain: dstChain,
                            src_asset_address: srcAssetAddress,
                            dst_asset_address: dstAssetAddress,
                            src_amount: srcAmount.toString(),
                            dst_amount: transformedQuote.dst_amount.toString(),
                            bond_amount: transformedQuote.bond_amount.toString(),
                            quote_time_ms: performance.now() - quoteStartTime,
                            has_target_address: !!targetAddress,
                            liquidity_source: transformedQuote.liquidity_source,
                            using_v2_api: true,
                        })

                        return {
                            status: ResultCode.success,
                            quote: transformedQuote,
                        }
                    } catch (transformError) {
                        logger.error('[getQuote] Failed to transform V2 quote response', transformError)

                        posthog.capture('quote_failed', {
                            error_type: 'TransformError',
                            error_message:
                                transformError instanceof Error ? transformError.message : 'Unknown transform error',
                            src_chain: srcChain,
                            dst_chain: dstChain,
                            using_v2_api: true,
                        })

                        // Return error when using V2 API
                        return {
                            status: ResultCode.failure,
                            quote: null,
                            errorMsg: 'Failed to process quote. Please try again later.',
                        }
                    }
                } else {
                    // Handle non-200 responses
                    logger.error(`[getQuote] V2 Quote API returned non-200 status: ${response.status}`, {
                        status: response.status,
                        statusText: response.statusText,
                    })

                    posthog.capture('quote_failed', {
                        error_type: 'ApiError',
                        status: response.status,
                        error_message: response.statusText,
                        src_chain: srcChain,
                        dst_chain: dstChain,
                        using_v2_api: true,
                    })

                    // Return error when using V2 API
                    return {
                        status: ResultCode.failure,
                        quote: null,
                        errorMsg: `API error: ${response.statusText || 'Unknown error'}`,
                    }
                }
            } catch (v2Error) {
                // Handle errors with V2 API call
                logger.error('[getQuote] Error calling V2 Quote API', {
                    error: v2Error instanceof Error ? v2Error.message : String(v2Error),
                })

                posthog.capture('quote_failed', {
                    error_type: 'NetworkError',
                    error_message: v2Error instanceof Error ? v2Error.message : 'Unknown error',
                    src_chain: srcChain,
                    dst_chain: dstChain,
                    using_v2_api: true,
                })

                // Return error when using V2 API
                return {
                    status: ResultCode.failure,
                    quote: null,
                    errorMsg: 'Network error when trying to get a quote. Please try again later.',
                }
            }
        } else {
            // Use legacy API if V2_MIGRATION is not enabled
            logger.info('[getQuote] Using legacy API for quote')
            return await getLegacyQuote(
                walletAddress,
                srcChain,
                dstChain,
                srcAssetAddress,
                dstAssetAddress,
                srcAmount,
                targetAddress,
                signal,
                quoteStartTime
            )
        }
    } catch (error) {
        // Catch any unexpected errors
        logger.error('[getQuote] Unexpected error', {
            error: error instanceof Error ? error.message : String(error),
        })

        posthog.capture('quote_failed', {
            error_type: 'UnexpectedError',
            error_message: error instanceof Error ? error.message : 'Unknown error',
            src_chain: srcChain,
            dst_chain: dstChain,
        })

        return {
            errorMsg: error instanceof Error ? error.message : 'An unexpected error occurred',
            status: ResultCode.failure,
            quote: null,
        }
    }
}

/**
 * Hook to fetch a quote based on provided parameters.
 *
 * This hook uses TanStack Query to perform a network request to fetch a quote.
 * It automatically handles errors, cancellation, and provides helper methods for
 * invalidating and refetching the query or forcefully canceling an ongoing request.
 *
 * @param {IQuoteParams | undefined} params - The parameters required to fetch a quote.
 *   Must be defined and have a non-zero `srcAmount`.
 * @param {Omit<UseQueryOptions<IQuoteResponse>, 'queryKey' | 'queryFn'>} [options] -
 *   Additional options for configuring the query.
 *
 * @returns {UseQueryResult<IQuoteResponse> & {
 *   data: IQuoteResponse,
 *   invalidateQuoteAndRefetch: (newParams: IQuoteParams) => Promise<void>,
 *   forceCancel: () => Promise<void>
 * }} An object containing the query result and the following helper functions:
 *
 * - **invalidateQuoteAndRefetch:** Invalidates the current quote query and prefetches a new one with updated parameters.
 * - **forceCancel:** Forcefully cancels an ongoing quote query.
 */
/**
 * Hook to check if liquidity is available for a specific token pair
 * Only performs actual checks when V2_MIGRATION is enabled
 *
 * @param srcChain Source chain ID
 * @param srcAssetAddress Source token address
 * @param dstChain Destination chain ID
 * @param dstAssetAddress Destination token address
 * @param options Additional query options
 * @returns Query result with liquidity availability information
 */
export const useLiquidityCheck = (
    srcChain?: string | number,
    srcAssetAddress?: string,
    dstChain?: string | number,
    dstAssetAddress?: string,
    options?: Omit<
        UseQueryOptions<{
            supported: boolean
            sources: string[]
            message: string
            error?: string
        }>,
        'queryKey' | 'queryFn'
    >
) => {
    // Only enable the query if we have all required parameters AND V2_MIGRATION is enabled
    const hasRequiredParams = !!srcChain && !!srcAssetAddress && !!dstChain && !!dstAssetAddress
    const isV2Enabled = isFeatureEnabled('V2_MIGRATION') && !!V2_API_URL
    const isEnabled = hasRequiredParams && isV2Enabled

    return useQuery<{
        supported: boolean
        sources: string[]
        message: string
        error?: string
    }>({
        queryKey: ['liquidityCheck', srcChain, srcAssetAddress, dstChain, dstAssetAddress],
        queryFn: async ({ signal }) => {
            if (!isEnabled) {
                throw new Error('Missing required parameters for liquidity check')
            }

            return checkLiquidityAvailability(srcChain!, srcAssetAddress!, dstChain!, dstAssetAddress!, signal)
        },
        enabled: isEnabled,
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 60 * 1000, // 1 minute
        refetchOnWindowFocus: false,
        ...options,
    })
}

export const useQuote = (
    params: IQuoteParams | undefined,
    isQuoteEnabled: boolean,
    options?: Omit<UseQueryOptions<IQuoteResponse>, 'queryKey' | 'queryFn'>
) => {
    const isEnabled = !!params && params.srcAmount !== BigInt(0) && isQuoteEnabled
    const queryClient = useQueryClient()

    // Warn when quote enabled but other conditions block the hook
    if (isQuoteEnabled && !params) {
        logger.warn('[useQuote] isQuoteEnabled=true but params is undefined')
    }
    if (isQuoteEnabled && params && params.srcAmount === BigInt(0)) {
        logger.warn('[useQuote] isQuoteEnabled=true but srcAmount is 0')
    }

    const query = useQuery<IQuoteResponse>({
        queryKey: ['quote', JSON.stringify(params)],
        queryFn: async ({ signal }) => {
            if (!isQuoteEnabled) {
                logger.info('[useQuote] Aborting query because isQuoteEnabled is false')
                throw new Error(`isQuoteEnabled is false`)
            } else {
                // Just to debug.
                logger.info('[useQuote] isQuoteEnabled=true, continuing...')
            }

            if (!params) {
                logger.error('Fetch initiated without params')
                // Throwing here to prevent an indefinitely loading query.
                throw new Error(`Quote parameters are required`)
            }

            if (params.srcAmount === BigInt(0)) {
                logger.error('[useQuote] srcAmount is 0')
                // Throwing here to prevent an indefinitely loading query.
                throw new Error(`Quote srcAmount is 0.`)
            }

            try {
                const result = await getQuote(params, signal)

                if (result.status === ResultCode.failure) {
                    logger.error(
                        `[useQuote] An error encountered while fetching quote: ${result.errorMsg ?? 'Unknown error.'}`
                    )
                }
                return result
            } catch (error) {
                // Log if the error is due to query cancellation.
                if (error instanceof Error && error.name === 'AbortError') {
                    logger.info('[useQuote] Query was forcefully cancelled.')
                }
                const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
                return {
                    status: ResultCode.failure,
                    quote: null,
                    errorMsg: errorMessage,
                }
            }
        },
        enabled: isEnabled,
        refetchOnMount: true,
        notifyOnChangeProps: ['data', 'error', 'isLoading', 'isFetching'],
        gcTime: 1000,
        initialData: {
            status: ResultCode.pending,
            quote: null,
            errorMsg: 'Uninitialized query call.',
        },
        ...options,
    })

    /**
     * Forcefully cancels the ongoing quote query.
     *
     * This function can be used to immediately abort a running query—such as in response to a user action or some other event.
     *
     * @returns {Promise<void>} A promise that resolves once the query has been cancelled.
     */
    const forceCancel = async (): Promise<void> => {
        logger.info('[useQuote] Force cancelling the quote query')
        await queryClient.cancelQueries({
            queryKey: ['quote'],
        })
    }

    /**
     * Invalidates the current quote query and prefetches a new one with updated parameters.
     *
     * Use this function when you need to refresh the quote with new parameters.
     *
     * @param {IQuoteParams} newParams - The new parameters for fetching the quote.
     * @returns {Promise<void>} A promise that resolves once the new quote query has been prefetched.
     */
    const invalidateQuoteAndRefetch = async (newParams: IQuoteParams): Promise<void> => {
        logger.info('[useQuote] Invalidating and refetching', { newParams })
        if (!isQuoteEnabled) {
            logger.info('[useQuote] Aborting invalidateQuoteAndRefetch because isQuoteEnabled is false')
            return
        }

        // Invalidate the current query
        await queryClient.invalidateQueries({ queryKey: ['quote'] })

        // Optionally clear stale data
        queryClient.setQueryData(['quote', JSON.stringify(newParams)], undefined)

        // Prefetch the new quote query
        await queryClient.prefetchQuery({
            queryKey: ['quote', JSON.stringify(newParams)],
            queryFn: async () => await getQuote(newParams),
        })
    }

    // Return the query object along with helper functions.
    return {
        ...query,
        invalidateQuoteAndRefetch,
        forceCancel,
        // Ensure `data` is always of type `IQuoteResponse`
        data: query.data || {
            status: ResultCode.failure,
            quote: null,
            errorMsg: '[useQuote] Uninitialized query call.',
        },
    } as UseQueryResult<IQuoteResponse> & {
        data: IQuoteResponse
        invalidateQuoteAndRefetch: (newParams: IQuoteParams) => Promise<void>
        forceCancel: () => Promise<void>
    }
}

export const apiNewPostReceiveCctpMessage = async (
    srcChain: string,
    burnTx: string,
    solana_token_owner_address?: string
) => {
    logger.info('[useQuote] apiNewPostReceiveCctpMessage called')
    try {
        const payload: Record<string, any> = {
            chain: srcChain.toLowerCase(),
            burn_tx: burnTx,
        }
        if (solana_token_owner_address) {
            payload.solana_token_owner_address = solana_token_owner_address
        }
        const response = await v1Api.post('/v1/orders/cctp', payload)
        if (response.status == 200 || response.status === 202) {
            const result = {
                message: 'Order accepted but will take 5-20 minutes to find a match.',
                status: ResultCode.pending,
                eta: response.data.eta,
                id: response.data.id,
            }
            return result
        }
    } catch (error) {
        logger.error('[useQuote] apiNewPostReceiveCctpMessage error')
        posthog.capture('[useQuote] apiNewPostReceiveCctpMessage Failure', {
            srcChain: srcChain,
            burnTx: burnTx,
            solana_token_owner_address: solana_token_owner_address,
        })
        return {
            message: 'Order will not be filled by the market maker at this time.',
            error: error,
            status: ResultCode.failure,
            eta: 0,
            id: null,
        }
    }
}
